import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LogOut, User, Globe2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Navbar() {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/chat/unread`, { withCredentials: true });
        if (!cancelled) setUnread(res.data?.total || 0);
      } catch (e) { /* not authed yet */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Check admin status once
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/me`, { withCredentials: true })
      .then((r) => setIsAdmin(!!r.data?.is_admin)).catch(() => setIsAdmin(false));
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  return (
    <header className="sticky top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate('/dashboard')}
          data-testid="navbar-logo"
        >
          <div className="w-10 h-10 rounded-xl bg-lime-500/20 flex items-center justify-center border border-lime-500/50">
            <Globe2 className="w-6 h-6 text-lime-400" />
          </div>
          <span className="text-xl font-black tracking-tight text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Worldwide Susu</span>
        </div>

        <div className="flex items-center space-x-2 md:space-x-4">
          <Button
            data-testid="nav-dashboard-button"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="rounded-full text-neutral-400 hover:text-white hover:bg-white/10 font-bold"
          >
            Dashboard
          </Button>
          <Button
            data-testid="nav-clubs-button"
            variant="ghost"
            onClick={() => navigate('/clubs')}
            className="rounded-full relative text-neutral-400 hover:text-white hover:bg-white/10 font-bold"
          >
            Clubs
            {unread > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-lime-500 text-black text-[10px] font-black rounded-full flex items-center justify-center"
                data-testid="nav-chat-unread-badge"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Button>
          <Button
            data-testid="nav-kids-button"
            variant="ghost"
            onClick={() => navigate('/kids')}
            className="rounded-full text-neutral-400 hover:text-white hover:bg-white/10 font-bold hidden sm:flex"
          >
            Kids
          </Button>
          {isAdmin && (
            <Button
              data-testid="nav-admin-button"
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="rounded-full text-rose-400 hover:text-white hover:bg-rose-500/20 font-bold"
            >
              Admin
            </Button>
          )}
          <Button
            data-testid="nav-investments-button"
            variant="ghost"
            onClick={() => navigate('/investments')}
            className="rounded-full text-neutral-400 hover:text-white hover:bg-white/10 font-bold hidden md:flex"
          >
            Investments
          </Button>
          <Button
            data-testid="nav-membership-button"
            variant="ghost"
            onClick={() => navigate('/membership')}
            className="rounded-full text-neutral-400 hover:text-white hover:bg-white/10 font-bold hidden md:flex"
          >
            Membership
          </Button>
          <Button
            data-testid="nav-withdrawals-button"
            variant="ghost"
            onClick={() => navigate('/withdrawals')}
            className="rounded-full text-neutral-400 hover:text-white hover:bg-white/10 font-bold hidden lg:flex"
          >
            Withdrawals
          </Button>

          <div className="relative ml-2">
            <Button
              data-testid="nav-profile-button"
              variant="ghost"
              onClick={() => setShowMenu(!showMenu)}
              className="rounded-full p-2 h-10 w-10 border border-white/10 hover:bg-white/10 hover:border-lime-500/50 transition-colors"
            >
              <Avatar className="w-full h-full rounded-full">
                <AvatarFallback className="bg-lime-500/20 text-lime-400 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5" />
                </AvatarFallback>
              </Avatar>
            </Button>

            {showMenu && (
              <div className="absolute right-0 mt-3 w-48 bg-[#111111] rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] border border-white/10 py-2 z-50 overflow-hidden backdrop-blur-xl">
                <button
                  data-testid="profile-menu-button"
                  onClick={() => {
                    setShowMenu(false);
                    navigate('/profile');
                  }}
                  className="w-full px-4 py-3 text-left text-neutral-300 hover:bg-white/5 hover:text-white flex items-center font-bold text-sm transition-colors"
                >
                  <User className="w-4 h-4 mr-3 text-lime-400" />
                  Profile
                </button>
                <div className="h-px w-full bg-white/5 my-1"></div>
                <button
                  data-testid="logout-button"
                  onClick={() => {
                    setShowMenu(false);
                    handleLogout();
                  }}
                  className="w-full px-4 py-3 text-left text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center font-bold text-sm transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
